import HomeView from '@/views/HomeView.vue'

export const routes = [
  {
    path: '/',
    name: 'home',
    component: HomeView,
    // Public: renders the existing marketing HomeView. App host: router/index.js's
    // beforeEach guard intercepts this route before it renders, sending authenticated
    // members to /current-sky and everyone else to /login -- see the "Host-based
    // route ownership" comment there.
    meta: { public: true },
  },
  {
    path: '/home',
    redirect: '/'
  },
  {
    path: '/signup',
    name: 'signup',
    component: () => import('@/views/Signup.vue')
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/Login.vue')
  },
  {
    path: '/about',
    name: 'about',
    component: () => import('@/views/AboutView.vue'),
    // Public-only: the app host redirects /about to the public site's /about rather
    // than maintaining two canonical About pages (see router/index.js's host guard).
    meta: { public: true, publicOnly: true },
  },
  {
    path: '/evolutionary-profile',
    name: 'evolutionary-profile',
    component: () => import('@/views/EvolutionaryProfileView.vue'),
  },
  {
    path: '/verify-email',
    name: 'verify-email',
    component: () => import('@/views/VerifyEmailView.vue'),
  },
  {
    path: '/verify-email-required',
    name: 'verify-email-required',
    component: () => import('@/views/VerifyEmailRequiredView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/create-chart',
    name: 'create-chart',
    component: () => import('@/views/NatalChartInputView.vue'),
    meta: { requiresAuth: true, requiresVerifiedEmail: true },
  },
  {
    path: '/personal-sky',
    name: 'personal-sky',
    component: () => import('@/views/PersonalSkyView.vue'),
    meta: { requiresAuth: true, requiresVerifiedEmail: true },
  },
  {
    path: '/personal-sky/evolutionary-profile',
    name: 'personal-sky-evolutionary-profile',
    component: () => import('@/views/PersonalSkyEvolutionaryProfileView.vue'),
    meta: { requiresAuth: true, requiresVerifiedEmail: true },
  },
  {
    path: '/custom-sky',
    name: 'custom-sky',
    component: () => import('@/views/CustomSkyView.vue'),
    meta: { requiresAuth: true, requiresVerifiedEmail: true },
  },
  {
    path: '/workspace',
    name: 'workspace',
    component: () => import('@/views/WorkspaceView.vue'),
    // Full-screen workspace framework: `fullscreen` tells App.vue to hide the
    // MainHeader/MainFooter chrome for this route only.
    //
    // Intentionally NOT `requiresAuth`: the route is reachable while logged out so
    // it can render the workspace-area shell ("SkySpeaks Workspaces" + Log in).
    // Private member workspace data is gated inside WorkspaceView (it mounts the
    // WorkspaceSession bootstrap only for an authenticated member with a resolved
    // id, and the workspace framework only once server state is confirmed), never
    // by this guard. `requiresVerifiedEmail` still bounces an
    // authenticated-unverified member to the verification gate.
    meta: { requiresVerifiedEmail: true, fullscreen: true },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/ProfileView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/account',
    name: 'account',
    component: () => import('@/views/AccountView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/current-sky',
    name: 'current-sky',
    component: () => import('@/views/CurrentSkyView.vue'),
    // Dual-host, but not dual-*policy*: public and unauthenticated on the public site,
    // a normal authenticated member feature on the app host. `public: true` only
    // grants host-ownership access (lets the route resolve on both hosts without a
    // cross-host redirect); `requiresAuthOnAppHost` is the separate flag
    // router/index.js's guard checks to require a session specifically when on the
    // app host, so an unauthenticated visitor cannot browse it merely by entering an
    // app.* URL.
    meta: { public: true, requiresAuthOnAppHost: true },
  },
  {
    path: '/public-person/:slug?',
    name: 'public-person',
    component: () => import('@/views/PublicPersonSkyView.vue'),
    // Ownership between public site and app is genuinely ambiguous -- not decided by
    // this task (flagged for clarification in the completion report). Exempted from
    // the host-ownership guard so it keeps working unchanged on whichever host reaches
    // it, pending a decision.
    meta: { hostAgnostic: true },
  },
  {
    path: '/public-event/:slug?',
    name: 'public-event',
    component: () => import('@/views/PublicEventSkyView.vue'),
    // See /public-person above -- same ambiguity, same exemption.
    meta: { hostAgnostic: true },
  },
  {
    path: '/admin/login',
    name: 'admin-login',
    component: () => import('@/views/admin/AdminLoginView.vue'),
  },
  {
    path: '/admin/unauthorized',
    name: 'admin-unauthorized',
    component: () => import('@/views/admin/AdminUnauthorizedView.vue'),
  },
  {
    path: '/admin/users',
    name: 'admin-users',
    component: () => import('@/views/admin/AdminUsersView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/users/:id',
    name: 'admin-user-edit',
    component: () => import('@/views/admin/AdminUserEditView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/ep-settings',
    name: 'admin-ep-settings',
    component: () => import('@/views/admin/AdminEpSettingsView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/ep-language-rules',
    name: 'admin-ep-language-rules',
    component: () => import('@/views/admin/AdminEpLanguageRulesView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/ep-language-rules/new',
    name: 'admin-ep-language-rule-new',
    component: () => import('@/views/admin/AdminEpLanguageRuleFormView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/ep-language-rules/:id',
    name: 'admin-ep-language-rule-edit',
    component: () => import('@/views/admin/AdminEpLanguageRuleFormView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/ep-test-preview',
    name: 'admin-ep-test-preview',
    component: () => import('@/views/admin/AdminEpTestPreviewView.vue'),
    meta: { requiresAdmin: true },
  },
  { path: '/skyview', redirect: '/personal-sky' },
  { path: '/natal_input', redirect: '/create-chart' },
  { path: '/public_sky', redirect: '/current-sky' },
  { path: '/public-sky', redirect: '/current-sky' },
  { path: '/personal_sky', redirect: '/custom-sky' },
  {
    path: '/begin',
    redirect: (to) => ({ path: '/evolutionary-profile', query: to.query, hash: to.hash }),
  },
  {
    path: '/:catchAll(.*)',
    component: () => import('@/components/common/NotFound.vue')
  },
]
